import { useUser } from 'src/store/modules/App/user'
import dayjs from '@utils/libraries/dayjs'
import { formatDate } from '@/utils/formats'
import { t } from '@/utils/helpers/translate'

/**
 * Function to return the range between dates in seconds
 * @param  {string} dateTime
 * @return {number}
 */
export const getDateTimeRange = (dateTime: string): number => {
  const pastDateTime = new Date(dateTime)
  const now = new Date()

  return now.getTime() - pastDateTime.getTime()
}

/**
 * Function to return the datetime ago
 * @param  {string} dateTime
 * @return {string}
 */
export const getDateTimeAgo = (dateTime: string | Date): string => {
  const userStore = useUser()

  const userLanguage = userStore?.language || 'en'
  return dayjs(dateTime).locale(userLanguage).fromNow()
}

export const getTimestampFromDate = (date: string): number =>
  Math.floor(Date.parse(date))

/**
 * Function to return the number of days of difference between two dates
 * @param  {string} baseDate
 * @param  {string} futureDate
 * @return {number}
 *
 * @example
 * getDiffBetweenDates('2023-01-26T20:14:30.614Z', '2023-02-25T17:14:30.000Z')
 * returns 30
 */
export const getDiffBetweenDates = (
  baseDate: string | Date | dayjs.Dayjs,
  futureDate: string | Date | dayjs.Dayjs,
): number => {
  const createdDate = dayjs.utc(baseDate)
  const diffDays = createdDate.diff(futureDate, 'day') * -1 + 1
  return diffDays
}

/**
 * Function to transform minutes in milliseconds
 * @param  {Number} minutes
 * @return {Number}
 */
export const getMinutesInMilliseconds = (minutes: number): number => {
  return minutes * 60000
}

/**
 * Function to return a range of dates
 * @param {dayjs.ConfigType} start the start date
 * @param {dayjs.ConfigType} end the end date
 * @param {string | null} format if should format the dates
 * @param {number} amount spacing between the returned list of dates
 * @param {dayjs.ManipulateType} unit by which unit the range should be
 * @return {(dayjs.Dayjs | string)[]} returns an array os dayjs object or an array of string if format is not null
 *
 * @example
 * const today = dayjs()
 *
 * getRangeOfDates('2023-08-08', '2023-08-14')
 * getRangeOfDates(today.subtract(6, 'days'), today)
 * getRangeOfDates(today.subtract(6, 'days'), today, 'DD/MM/YYYY')
 * getRangeOfDates(today.subtract(6, 'days'), today, 'DD/MM/YYYY', 2)
 * getRangeOfDates(today.subtract(11, 'months'), today, 'DD/MM/YYYY', 1, 'months')
 */
export const getRangeOfDates = (
  start: dayjs.ConfigType = null,
  end: dayjs.ConfigType = null,
  format: string | null = null,
  amount: number = 1,
  unit: dayjs.ManipulateType = 'days',
): (dayjs.Dayjs | string)[] => {
  const startDate = dayjs(start)
  const endDate = dayjs(end)

  if (!startDate.isValid() || !endDate.isValid()) {
    throw new Error('Start and End parameters must be a valid dayjs date')
  }

  const startOfRange = dayjs.min(startDate, endDate)!
  const endOfRange = dayjs.max(startDate, endDate)!
  const ranges: (dayjs.Dayjs | string)[] = []

  let currentDate = startOfRange

  do {
    ranges.push(format ? currentDate.format(format) : currentDate)
    currentDate = currentDate.add(amount, unit)
  } while (currentDate.isSameOrBefore(endOfRange))

  return ranges
}

/**
 * Function that formats the datetime ago depending on how long ago it was
 * Today, 07:00am
 * Yesterday, 1:00pm
 * Mar 25, 2024, 1:00pm
 *
 * @param  {String} date to be converted
 * @return {String}
 */

export const getNaturalDateTime = (dateString: string): string => {
  const date = dayjs(dateString)
  const today = dayjs().startOf('day')
  const yesterday = dayjs().subtract(1, 'day').startOf('day')

  if (date.isSame(today, 'day')) {
    return t('Today, {time}', { time: formatDate(date, 'hh:mma') })
  }

  if (date.isSame(yesterday, 'day')) {
    return t('Yesterday, {time}', { time: formatDate(date, 'hh:mma') })
  }

  return formatDate(date, 'MMM D, YYYY, hh:mma')
}
